import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Storage from '../utils/storageES.js'
import { pdf } from '@react-pdf/renderer'
import TripReportPDF_Magazine from './TripReportPDF_Magazine.jsx'
import PremiumGate from './PremiumGate.jsx'
import dataManager from '../utils/DataManager.js'
import storageManager from '../utils/storageManager.js'
import SealBookModal from './SealBookModal.jsx'
import BookletPDF from './BookletPDF.jsx'
import { unsealTrip } from '../utils/sealManager.js'
import { getAtlas } from '../utils/atlasManager.js'
import { runTask, isRunning } from '../utils/backgroundTasks.js'

export default function TripSummary({ userData, onNavigate }) {
  const { t, i18n } = useTranslation();

  // PDF/booklet now run as background tasks (progress → native notification,
  // completion → BackgroundTaskLayer chip). We only track a local "generating"
  // flag to disable the buttons; it resets when the pdf task finishes.
  const [generating, setGenerating] = useState(false)
  useEffect(() => {
    const reset = (e) => { if (e.detail?.kind === 'pdf') setGenerating(false) }
    window.addEventListener('wayfare-task-done', reset)
    window.addEventListener('wayfare-task-error', reset)
    return () => {
      window.removeEventListener('wayfare-task-done', reset)
      window.removeEventListener('wayfare-task-error', reset)
    }
  }, [])
  const [sealedAt, setSealedAt] = useState(null)
  const [sealTripObj, setSealTripObj] = useState(null) // full trip loaded when opening the seal modal

  const [tripStats, setTripStats] = useState({
    duration: 0,
    totalSpent: 0,
    budgetUtilization: 0,
    notesCount: 0,
    photosCount: 0,
    challengesCompleted: 0,
    challengesEligible: 0,
    activitiesPlanned: 0,
    activitiesCompleted: 0
  })

  const [tripData, setTripData] = useState({
    notes: [],
    photos: [],
    challenges: [],
    budget: {},
    reflections: []
  })

  useEffect(() => {
    loadTripSummaryData()
  }, [userData])

  const loadTripSummaryData = async () => {
    try {
      // Use async version to read real data from IndexedDB (sync version returns stale cache)
      const data = await Storage.getTripDataAsync()
      // Inject tripId so cleanTripData can fetch all photos from DataManager
      const dataWithId = { ...data, tripId: data.tripId || userData?.tripId }
      setTripData(dataWithId)
      setSealedAt(data.sealedAt || null)

      // Get budget data
      const budgetData = Storage.get('budgetData', {
        dailyBudget: 0,
        totalSpent: 0,
        spentToday: 0
      })

      // Calculate trip duration
      const duration = calculateTripDuration()

      // Calculate challenge stats
      const challengeNotes = data.notes.filter(note => note.isChallenge === true) || []
      const accomplishedChallenges = challengeNotes.filter(note =>
        note.challengeData && note.challengeData.submitted === true
      ) || []

      // Calculate stats
      const stats = {
        duration: duration,
        totalSpent: budgetData.totalSpent || 0,
        budgetUtilization: budgetData.dailyBudget > 0 ?
          Math.round((budgetData.totalSpent / (budgetData.dailyBudget * duration)) * 100) : 0,
        notesCount: data.notes?.length || 0,
        photosCount: data.totalPhotoCount || data.photos?.length || 0,
        challengesCompleted: challengeNotes.length,
        challengesEligible: accomplishedChallenges.length,
        activitiesPlanned: 0,
        activitiesCompleted: 0
      }

      setTripStats(stats)
    } catch (error) {
      console.error('Error loading trip summary data:', error)
    }
  }

  const handleOpenSeal = async () => {
    const tripId = tripData.tripId || userData?.tripId
    if (!tripId) return
    const trip = await storageManager.getTrip(tripId).catch(() => null)
    if (trip) setSealTripObj(trip)
  }

  const handleReopen = async () => {
    const tripId = tripData.tripId || userData?.tripId
    if (!tripId) return
    await unsealTrip(tripId).catch(() => {})
    setSealedAt(null)
  }

  const handleGenerateBooklet = () => {
    if (isRunning('pdf-booklet')) return
    setGenerating(true)
    runTask({
      id: 'pdf-booklet', kind: 'pdf', label: t('tasks.bookletLabel'),
      run: async (onProgress) => {
        onProgress({ phase: t('tasks.rendering') })
        const notes = tripData.notes || []
        const memories = notes.filter(n => !n.isActivity && !n.isChallenge)
        const offRoute = memories.filter(n => n.offRoute).length
        const atlas = await getAtlas().catch(() => null)
        const rec = atlas?.records || {}
        const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' }) : ''

        const records = [
          rec.longestTripDays && { label: t('atlas.longestTrip'), value: `${rec.longestTripDays.value} ${t('atlas.daysUnit')}`, sub: rec.longestTripDays.label },
          rec.mostMemoriesInTrip && rec.mostMemoriesInTrip.value > 0 && { label: t('atlas.mostMemories'), value: `${rec.mostMemoriesInTrip.value} ${t('atlas.memoriesUnit')}`, sub: rec.mostMemoriesInTrip.label },
          rec.mostCitiesInCountry && { label: t('atlas.mostCitiesInCountry'), value: `${rec.mostCitiesInCountry.value} ${t('atlas.citiesUnit')}`, sub: rec.mostCitiesInCountry.country },
          rec.lastSealedAt && { label: t('atlas.lastSeal'), value: fmtDate(rec.lastSealedAt) },
        ].filter(Boolean)

        const booklet = {
          title: userData?.destination || t('booklet.title'),
          subtitle: [fmtDate(userData?.startDate), fmtDate(userData?.endDate)].filter(Boolean).join(' – '),
          headings: { figures: t('booklet.figuresHeading'), records: t('booklet.recordsHeading'), threads: t('booklet.threadsHeading') },
          figures: [
            { value: tripStats.duration || 0,                 label: t('atlas.days') },
            { value: memories.length,                          label: t('atlas.memories') },
            { value: (tripData.reflections || []).length,      label: t('atlas.reflections') },
            { value: (tripData.revealedPois || []).length,     label: t('atlas.gemsRevealed') },
            { value: (tripData.surfacedPois || []).length,     label: t('atlas.gemsWhispered') },
            { value: offRoute,                                 label: t('atlas.offRoute') },
          ],
          records,
          threads: (tripData.openThreads || []).map(th => th.text).filter(Boolean),
        }

        const blob = await pdf(<BookletPDF booklet={booklet} />).toBlob()
        return { kind: 'pdf', blob, filename: `${userData?.destination || 'Trip'}_Booklet.pdf`, doneText: t('tasks.pdfReady') }
      },
    })
  }

  const calculateTripDuration = () => {
    if (!userData.startDate || !userData.endDate) return 0
    const start = new Date(userData.startDate)
    const end = new Date(userData.endDate)
    const diffTime = Math.abs(end - start)
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
  }

  const getTripProgress = () => {
    if (!userData.startDate) return 0
    const start = new Date(userData.startDate)
    const today = new Date()
    const diffTime = Math.abs(today - start)
    const daysPassed = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return Math.min(Math.round((daysPassed / tripStats.duration) * 100), 100)
  }

  const getCityData = async (userData) => {
    try {
      console.log('🏙️ [PDF] Looking for city data for:', userData.destination, 'in', userData.country);

      // Map country names to actual JSON file names
      const countryFileMap = {
        // Western Europe
        'Spain': 'spain',
        'España': 'spain',
        'France': 'france',
        'Francia': 'france',
        'Italy': 'italia',
        'Italia': 'italia',
        'Germany': 'germany',
        'Deutschland': 'germany',
        'Alemania': 'germany',
        'Portugal': 'portugal',
        'Netherlands': 'netherlands',
        'Nederland': 'netherlands',
        'Países Bajos': 'netherlands',
        'Belgium': 'belgium',
        'Bélgica': 'belgium',
        'Luxembourg': 'luxembourg',
        'Luxemburgo': 'luxembourg',
        'Switzerland': 'switzerland',
        'Suiza': 'switzerland',
        'Austria': 'austria',

        // United Kingdom & Ireland
        'England': 'england',
        'Inglaterra': 'england',
        'Ireland': 'ireland',
        'Irlanda': 'ireland',

        // Nordic Countries
        'Sweden': 'sweden',
        'Suecia': 'sweden',
        'Norway': 'norway',
        'Noruega': 'norway',
        'Denmark': 'denmark',
        'Dinamarca': 'denmark',
        'Finland': 'finland',
        'Finlandia': 'finland',
        'Iceland': 'iceland',
        'Islandia': 'iceland',
        'Faroe Islands': 'faroe islands',
        'Islas Feroe': 'faroe islands',
        'Greenland': 'greenland',
        'Groenlandia': 'greenland',

        // Eastern Europe
        'Poland': 'poland',
        'Polonia': 'poland',
        'Czech Republic': 'czech_republic',
        'República Checa': 'czech_republic',
        'Czechia': 'czech_republic',
        'Slovakia': 'slovakia',
        'Eslovaquia': 'slovakia',
        'Hungary': 'hungary',
        'Hungría': 'hungary',
        'Romania': 'romania',
        'Rumania': 'romania',
        'Bulgaria': 'bulgaria',
        'Ukraine': 'ukraine',
        'Ucrania': 'ukraine',
        'Belarus': 'belarus',
        'Bielorrusia': 'belarus',
        'Moldova': 'moldova',

        // Baltic States
        'Estonia': 'estonia',
        'Latvia': 'latvia',
        'Letonia': 'latvia',
        'Lithuania': 'lituania',
        'Lituania': 'lituania',

        // Southern Europe
        'Greece': 'greece',
        'Grecia': 'greece',
        'Croatia': 'croatia',
        'Croacia': 'croatia',
        'Slovenia': 'slovenia',
        'Eslovenia': 'slovenia',
        'Bosnia and Herzegovina': 'bosnia and herzegovina',
        'Bosnia y Herzegovina': 'bosnia and herzegovina',
        'Serbia': 'serbia',
        'Montenegro': 'montenegro',
        'Albania': 'albania',
        'North Macedonia': 'north macedonia',
        'Macedonia del Norte': 'north macedonia',

        // Microstates
        'Monaco': 'monaco',
        'Mónaco': 'monaco',
        'Vatican City': 'vatican city',
        'Ciudad del Vaticano': 'vatican city',
        'San Marino': 'san marino',
        'Andorra': 'andorra',
        'Liechtenstein': 'liechenstein',
        'Gibraltar': 'gibraltar',
        'Malta': 'malta'
      };

      const countryFileName = countryFileMap[userData.country];
      if (!countryFileName) {
        console.log('🏙️ [PDF] No file mapping for country:', userData.country);
        return null;
      }

      try {
        // Get current language (fallback to 'en' if not set)
        const currentLanguage = i18n.language || 'en';
        console.log('🌍 [PDF] Current language:', currentLanguage);

        // Use relative path with language folder
        const url = `${import.meta.env.BASE_URL}TRIP/${currentLanguage}/${countryFileName}.json`;
        console.log('🏙️ [PDF] Fetching from:', url);
        const response = await fetch(url);

        if (!response.ok) {
          console.log('🏙️ [PDF] Failed to fetch:', response.status, response.statusText);
          return null;
        }

        const countryData = await response.json();
        console.log('🏙️ [PDF] Country data loaded, cities available:', countryData.cities?.length || 0);

        // Check if this country data contains our city
        const cityData = countryData.cities?.find(c =>
          c.city.toLowerCase() === userData.destination.toLowerCase()
        );

        if (cityData) {
          console.log('🏙️ [PDF] Found city data for:', cityData.city);
          console.log('🏙️ [PDF] City data keys:', Object.keys(cityData));
          return cityData;
        } else {
          console.log('🏙️ [PDF] City not found. Available cities:',
            countryData.cities?.map(c => c.city).slice(0, 5) || []);
          return null;
        }
      } catch (err) {
        console.error(`🏙️ [PDF] Error loading ${countryFileName}.json:`, err);
        return null;
      }
    } catch (error) {
      console.error('🏙️ [PDF] Error fetching city data:', error);
      return null;
    }
  }

  // Convert any image (Blob or base64 string) to an EXIF-stripped JPEG base64.
  // Drawing through canvas removes EXIF and auto-corrects orientation (Chrome 81+, Safari 15+).
  const toJpegBase64 = (src) => new Promise((resolve) => {
    try {
      const img = new window.Image();
      img.onload = () => {
        try {
          // Cap at 1600px on the longest side to keep PDF size sane
          const MAX = 1600;
          let { width, height } = img;
          if (width > MAX || height > MAX) {
            const ratio = Math.min(MAX / width, MAX / height);
            width  = Math.round(width  * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width  = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.88));
        } catch (_) { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = typeof src === 'string' ? src : URL.createObjectURL(src);
    } catch (_) { resolve(null); }
  });

  // Resolve a single photo entry to { ...photo, data: jpegBase64 } or null.
  // Handles three shapes:
  //   1. { data: base64string, ... }         — full photo already in memory
  //   2. { data: Blob, ... }                 — photo from IndexedDB getPhotos()
  //   3. { id, timestamp }                   — reference only (post-May-2026 note storage)
  const resolvePhoto = async (photo) => {
    try {
      let src = photo?.data;

      // Shape 3: reference without data — fetch from IndexedDB
      if (!src && photo?.id) {
        const record = await dataManager.getPhoto(photo.id);
        src = record?.data;   // Blob
      }

      if (!src) return null;

      const jpeg = await toJpegBase64(src);
      if (!jpeg || jpeg.length < 200) return null;

      return { ...photo, data: jpeg };
    } catch (_) {
      return null;
    }
  };

  // Resolve + EXIF-correct all photos in the trip data.
  // onProgress(current, total) is called after each photo conversion.
  const cleanTripData = async (data, onProgress) => {
    const cleanedData = { ...data };
    const tripId = data.tripId || userData?.tripId;

    // Pre-fetch all top-level photos so we can count them before starting
    let rawPhotos = [];
    try {
      if (tripId) rawPhotos = await dataManager.getPhotos(tripId);
    } catch (_) {}

    // Count total photos to convert (note refs + top-level)
    const notePhotoCount = (data.notes || []).reduce((n, note) => n + (note.photos?.length || 0), 0);
    const total = notePhotoCount + rawPhotos.length;
    let current = 0;

    const tracked = async (photo) => {
      const result = await resolvePhoto(photo);
      current++;
      onProgress?.(current, total);
      return result;
    };

    // Resolve photo refs inside notes
    if (Array.isArray(cleanedData.notes)) {
      cleanedData.notes = await Promise.all(
        cleanedData.notes.map(async (note) => {
          if (!note.photos?.length) return note;
          const resolved = (await Promise.all(note.photos.map(tracked))).filter(Boolean);
          return { ...note, photos: resolved };
        })
      );
    }

    // Resolve top-level photos
    if (rawPhotos.length > 0) {
      cleanedData.photos = (await Promise.all(rawPhotos.map(tracked))).filter(Boolean);
    } else if (Array.isArray(cleanedData.photos)) {
      cleanedData.photos = (await Promise.all(cleanedData.photos.map(tracked))).filter(Boolean);
    }

    return cleanedData;
  };

  const handleGeneratePDF = () => {
    if (isRunning('pdf-report')) return
    setGenerating(true)
    runTask({
      id: 'pdf-report', kind: 'pdf', label: t('tasks.pdfLabel'),
      run: async (onProgress) => {
        onProgress({ phase: t('tasks.preparing') })
        const cityData = await getCityData(userData)
        const cleanedTripData = await cleanTripData(tripData, (current, total) => {
          onProgress({ phase: t('tasks.photos'), current, total })
        })
        onProgress({ phase: t('tasks.rendering') })
        const blob = await pdf(
          <TripReportPDF_Magazine
            userData={userData}
            tripStats={tripStats}
            tripData={cleanedTripData}
            cityData={cityData}
          />
        ).toBlob()
        return { kind: 'pdf', blob, filename: `${userData.destination}_Trip_Report.pdf`, doneText: t('tasks.pdfReady') }
      },
    })
  }

  return (
    <div className="trip-summary-screen">
      <div className="trip-summary-header">
        <button
          className="back-button"
          onClick={() => onNavigate('home')}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            background: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 12px',
            fontSize: '14px',
            fontWeight: '600',
            color: '#8B7355',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {t('tripSummary.back')}
        </button>

        <div className="summary-title" style={{
          paddingTop: '60px'
        }}>
          <h1 style={{
            fontFamily: "'Noto Serif', serif",
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>{t('tripSummary.title')}</h1>
          <p>{userData.destination}, {userData.country}</p>
          <div className="trip-dates">
            {userData.startDate && userData.endDate && (
              <span>
                {new Date(userData.startDate).toLocaleDateString()} - {new Date(userData.endDate).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="trip-summary-content">
        {/* Trip Progress */}
        <div className="progress-section">
          <h3>{t('tripSummary.tripProgress')}</h3>
          <div className="progress-bar" style={{
            background: '#f0f0f0',
            borderRadius: '10px',
            height: '20px',
            marginBottom: '8px'
          }}>
            <div style={{
              background: '#C4714A',
              height: '100%',
              width: `${getTripProgress()}%`,
              borderRadius: '10px',
              transition: 'width 0.3s ease'
            }}></div>
          </div>
          <p>{getTripProgress()}% complete • Day {Math.min(Math.ceil((new Date() - new Date(userData.startDate)) / (1000 * 60 * 60 * 24)) + 1, tripStats.duration)} of {tripStats.duration}</p>
        </div>

        {/* Key Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-value">€{tripStats.totalSpent}</div>
            <div className="stat-label">{t('tripSummary.totalSpent')}</div>
            <div className="stat-detail">{tripStats.budgetUtilization}% {t('tripSummary.ofBudget')}</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-value">{tripStats.challengesCompleted}</div>
            <div className="stat-label">{t('tripSummary.challenges')}</div>
            <div className="stat-detail">{tripStats.challengesEligible} {t('tripSummary.prizeEligible')}</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">📝</div>
            <div className="stat-value">{tripStats.notesCount}</div>
            <div className="stat-label">{t('tripSummary.memories')}</div>
            <div className="stat-detail">{tripStats.photosCount} {t('tripSummary.photos')}</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">📍</div>
            <div className="stat-value">{tripStats.activitiesCompleted}</div>
            <div className="stat-label">{t('tripSummary.activities')}</div>
            <div className="stat-detail">{t('tripSummary.of')} {tripStats.activitiesPlanned} {t('tripSummary.planned')}</div>
          </div>
        </div>

        {/* Recent Highlights */}
        <div className="highlights-section">
          <h3>{t('tripSummary.recentHighlights')}</h3>
          <div className="highlights-list">
            {tripData.notes.slice(-3).reverse().map((note, index) => (
              <div key={note.id} className="highlight-item">
                <div className="highlight-icon">
                  {note.isChallenge ? '🎯' : '📝'}
                </div>
                <div className="highlight-content">
                  <div className="highlight-title">{note.title}</div>
                  <div className="highlight-date">Day {note.tripDay}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* PDF Generation */}
        <PremiumGate feature="pdf_export" onNavigate={onNavigate}>
          <div className="pdf-section">
            <h3>{t('tripSummary.shareJourney')}</h3>
            <p>{t('tripSummary.pdfDescription')}</p>
            <button
              className="generate-pdf-button"
              onClick={handleGeneratePDF}
              disabled={generating}
              style={{
                background: generating ? '#ccc' : '#C4714A',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: generating ? 'not-allowed' : 'pointer',
                width: '100%',
                marginTop: '16px'
              }}
            >
              {generating ? t('tasks.generating') : t('tripSummary.generatePDF')}
            </button>
          </div>
        </PremiumGate>

        {/* Seal the book — Sprint 2 */}
        <div className="seal-section" style={{ marginTop: '20px', background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          {sealedAt ? (
            <>
              <h3 style={{ margin: '0 0 6px', color: '#2D5A6E' }}>📕 {t('seal.sealed')}</h3>
              <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#8B7355' }}>
                {t('seal.sealedOn', { date: new Date(sealedAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }) })}
              </p>
              <PremiumGate feature="pdf_export" onNavigate={onNavigate}>
                <button
                  onClick={handleGenerateBooklet}
                  disabled={generating}
                  style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: generating ? '#ccc' : '#C4714A', color: 'white', fontSize: '15px', fontWeight: 700, cursor: generating ? 'not-allowed' : 'pointer', marginBottom: '10px' }}
                >
                  🖨️ {generating ? t('tasks.generating') : t('booklet.generate')}
                </button>
              </PremiumGate>
              <button
                onClick={handleReopen}
                style={{ width: '100%', padding: '13px', borderRadius: '12px', border: '1.5px solid #5B8FA8', background: 'transparent', color: '#2D5A6E', fontSize: '15px', fontWeight: 600, cursor: 'pointer' }}
              >
                {t('seal.reopen')}
              </button>
            </>
          ) : (
            <>
              <h3 style={{ margin: '0 0 6px', color: '#8B7355' }}>{t('seal.sealBook')}</h3>
              <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#8B7355' }}>{t('seal.sealPrompt')}</p>
              <button
                onClick={handleOpenSeal}
                style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: '#C4714A', color: 'white', fontSize: '16px', fontWeight: 700, cursor: 'pointer' }}
              >
                📕 {t('seal.sealBook')}
              </button>
            </>
          )}
        </div>
      </div>

      {sealTripObj && (
        <SealBookModal
          trip={sealTripObj}
          onClose={() => setSealTripObj(null)}
          onSealed={(res) => { setSealTripObj(null); setSealedAt(res?.sealedAt || new Date().toISOString()); if (res?.wasActive && onNavigate) onNavigate('mytrips'); }}
        />
      )}

    </div>
  )
}